import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class EventinfoText extends BaseObj {
    public ID: number;
    public __id: number;

    public Active: boolean;
    public __active: boolean;

    public Created: Date;
    public __created: Date;

    public EventID: number;
    public __eventid: number;

    public Icon: string | null;
    public __icon: string | null;

    public IconText: string | null;
    public __icontext: string | null;

    public Ingress: string | null;
    public __ingress: string | null;

    public Language: string | null;
    public __language: string | null;

    public LastChanged: Date;
    public __lastchanged: Date;

    public Sort: number | null;
    public __sort: number | null;

    public Text: string | null;
    public __text: string | null;

    public Title: string | null;
    public __title: string | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Active?: boolean;
        Created?: Date;
        EntityState?: EntityState;
        EventID?: number;
        Icon?: string | null;
        IconText?: string | null;
        Ingress?: string | null;
        Language?: string | null;
        LastChanged?: Date;
        Sort?: number | null;
        Text?: string | null;
        Title?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<EventinfoText>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('EventinfoText tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Active = values.Active ?? false;
        this.__active = values.Active ?? false;
        Object.defineProperty(this, 'Active', {
            get(): boolean {
                return this.__active;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Active', this.__active, value);
                this.__active = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Created = values.Created ?? new Date('1970-01-01T00:00:00.000');
        this.__created = values.Created ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'Created', {
            get(): Date {
                return this.__created;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('Created', this.__created, value);
                this.__created = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Icon = values.Icon ?? null;
        this.__icon = values.Icon ?? null;
        Object.defineProperty(this, 'Icon', {
            get(): string | null {
                return this.__icon;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Icon', this.__icon, value);
                this.__icon = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IconText = values.IconText ?? null;
        this.__icontext = values.IconText ?? null;
        Object.defineProperty(this, 'IconText', {
            get(): string | null {
                return this.__icontext;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('IconText', this.__icontext, value);
                this.__icontext = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Ingress = values.Ingress ?? null;
        this.__ingress = values.Ingress ?? null;
        Object.defineProperty(this, 'Ingress', {
            get(): string | null {
                return this.__ingress;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Ingress', this.__ingress, value);
                this.__ingress = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Language = values.Language ?? '';
        this.__language = values.Language ?? '';
        Object.defineProperty(this, 'Language', {
            get(): string | null {
                return this.__language;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Language', this.__language, value);
                this.__language = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LastChanged = values.LastChanged ?? new Date('1970-01-01T00:00:00.000');
        this.__lastchanged = values.LastChanged ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'LastChanged', {
            get(): Date {
                return this.__lastchanged;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('LastChanged', this.__lastchanged, value);
                this.__lastchanged = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Sort = values.Sort ?? null;
        this.__sort = values.Sort ?? null;
        Object.defineProperty(this, 'Sort', {
            get(): number | null {
                return this.__sort;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Sort', this.__sort, value);
                this.__sort = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Text = values.Text ?? null;
        this.__text = values.Text ?? null;
        Object.defineProperty(this, 'Text', {
            get(): string | null {
                return this.__text;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Text', this.__text, value);
                this.__text = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Title = values.Title ?? '';
        this.__title = values.Title ?? '';
        Object.defineProperty(this, 'Title', {
            get(): string | null {
                return this.__title;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Title', this.__title, value);
                this.__title = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Active": ${JSON.stringify(this.Active)}` +
            ',' +
            `"Created": ${'"' + formatDateIso(this.Created) + '"'}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"Icon": ${JSON.stringify(this.Icon)}` +
            ',' +
            `"IconText": ${JSON.stringify(this.IconText)}` +
            ',' +
            `"Ingress": ${JSON.stringify(this.Ingress)}` +
            ',' +
            `"Language": ${JSON.stringify(this.Language)}` +
            ',' +
            `"LastChanged": ${'"' + formatDateIso(this.LastChanged) + '"'}` +
            ',' +
            `"Sort": ${JSON.stringify(this.Sort)}` +
            ',' +
            `"Text": ${JSON.stringify(this.Text)}` +
            ',' +
            `"Title": ${JSON.stringify(this.Title)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of EventinfoText from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Active?: boolean;
        Created?: Date;
        EventID?: number;
        Icon?: string | null;
        IconText?: string | null;
        Ingress?: string | null;
        Language?: string | null;
        LastChanged?: Date;
        Sort?: number | null;
        Text?: string | null;
        Title?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): EventinfoText {
        return new EventinfoText(values);
    }

    public static _FromServerJson(json: any): EventinfoText {
        return new EventinfoText({
            ID: json.ID,
            Active: json.Active,
            Created: json.Created instanceof Date ? json.Created : new Date(json.Created),
            EventID: json.EventID,
            Icon: json.Icon ?? '',
            IconText: json.IconText ?? '',
            Ingress: json.Ingress ?? '',
            Language: json.Language ?? '',
            LastChanged: json.LastChanged instanceof Date ? json.LastChanged : new Date(json.LastChanged),
            Sort: json.Sort ?? null,
            Text: json.Text ?? '',
            Title: json.Title ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of EventinfoText from another instance of EventinfoText.
     * @param other The EventinfoText you want to clone
     * @returns A cloned EventinfoText
     */
    public static FromOther(other: EventinfoText): EventinfoText {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return EventinfoText._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return EventinfoText.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'Active'
            | 'Created'
            | 'EventID'
            | 'Icon'
            | 'IconText'
            | 'Ingress'
            | 'Language'
            | 'LastChanged'
            | 'Sort'
            | 'Text'
            | 'Title'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'EventinfoText';
}
